import { sql } from "drizzle-orm";
import {
  check,
  boolean,
  date,
  index,
  integer,
  jsonb,
  pgTable,
  text,
  timestamp,
  uniqueIndex,
  varchar,
} from "drizzle-orm/pg-core";
import {
  organisations,
  teachingAssignments,
  tutorProfiles,
  users,
} from "./schemaBase.js";

export type TeacherHandoverSelection = {
  goalIds: string[];
  repertoireIds: string[];
  practiceTodoIds: string[];
  appointmentIds: string[];
};

export type TeacherHandoverAssignmentVersion = {
  id: string;
  version: number;
};

/**
 * A narrow, owner-authorised share of selected current teaching context.
 * The anchor source assignment is the learner link so student merges preserve
 * the relationship; sourceAssignments records every active service row for the
 * handed-over instrument. Historic notes, messages, bookings and credits are
 * deliberately not copied into this record.
 */
export const teacherHandoverShares = pgTable(
  "teacher_handover_shares",
  {
    id: varchar("id")
      .primaryKey()
      .default(sql.raw("gen_random_uuid()::text")),
    organisationId: varchar("organisation_id")
      .notNull()
      .references(() => organisations.id, { onDelete: "cascade" }),
    sourceAssignmentId: varchar("source_assignment_id")
      .notNull()
      .references(() => teachingAssignments.id, { onDelete: "restrict" }),
    sourceAssignmentVersion: integer("source_assignment_version").notNull(),
    sourceAssignments: jsonb("source_assignments")
      .$type<TeacherHandoverAssignmentVersion[]>()
      .notNull()
      .default(sql`'[]'::jsonb`),
    destinationTutorProfileId: varchar("destination_tutor_profile_id")
      .notNull()
      .references(() => tutorProfiles.id, { onDelete: "restrict" }),
    destinationAssignmentId: varchar("destination_assignment_id")
      .references(() => teachingAssignments.id, { onDelete: "restrict" }),
    destinationAssignmentCreated: boolean("destination_assignment_created").notNull().default(false),
    assignmentTransferState: text("assignment_transfer_state").notNull().default("not_scheduled"),
    instrument: text("instrument").notNull(),
    effectiveDate: date("effective_date").notNull(),
    summary: text("summary").notNull(),
    selectedContext: jsonb("selected_context")
      .$type<TeacherHandoverSelection>()
      .notNull(),
    idempotencyKey: varchar("idempotency_key", { length: 36 }).notNull(),
    requestFingerprint: varchar("request_fingerprint", { length: 64 }).notNull(),
    createdByUserId: varchar("created_by_user_id").references(() => users.id, {
      onDelete: "set null",
    }),
    createdAt: timestamp("created_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
    revokedAt: timestamp("revoked_at", { withTimezone: true }),
    revokedByUserId: varchar("revoked_by_user_id").references(() => users.id, {
      onDelete: "set null",
    }),
  },
  (t) => [
    uniqueIndex("teacher_handover_shares_org_idempotency_idx").on(
      t.organisationId,
      t.idempotencyKey,
    ),
    uniqueIndex("teacher_handover_shares_active_source_idx")
      .on(t.organisationId, t.sourceAssignmentId)
      .where(sql`${t.revokedAt} is null and ${t.assignmentTransferState} in ('scheduled', 'effective')`),
    index("teacher_handover_shares_source_created_idx").on(
      t.organisationId,
      t.sourceAssignmentId,
      t.createdAt.desc(),
    ),
    index("teacher_handover_shares_destination_created_idx").on(
      t.organisationId,
      t.destinationTutorProfileId,
      t.createdAt.desc(),
    ),
    check(
      "teacher_handover_shares_source_assignments_check",
      sql.raw("jsonb_typeof(source_assignments) = 'array'"),
    ),
    check(
      "teacher_handover_shares_assignment_transfer_check",
      sql.raw("(assignment_transfer_state in ('scheduled', 'effective', 'cancelled') and destination_assignment_id is not null) or (assignment_transfer_state = 'not_scheduled' and destination_assignment_id is null and destination_assignment_created = false)"),
    ),
    check(
      "teacher_handover_shares_source_version_check",
      sql.raw("source_assignment_version > 0"),
    ),
    check(
      "teacher_handover_shares_summary_check",
      sql.raw("length(trim(summary)) between 1 and 2000"),
    ),
    check(
      "teacher_handover_shares_instrument_check",
      sql.raw("length(trim(instrument)) > 0"),
    ),
    check(
      "teacher_handover_shares_request_fingerprint_check",
      sql.raw("request_fingerprint ~ '^[0-9a-f]{64}$'"),
    ),
    check(
      "teacher_handover_shares_selection_check",
      sql.raw("jsonb_typeof(selected_context) = 'object'"),
    ),
    check(
      "teacher_handover_shares_revocation_check",
      sql.raw("(revoked_at is null and revoked_by_user_id is null) or revoked_at is not null"),
    ),
  ],
);
